import {
  CORE_PHASE_COLORS,
  type CreatePipelineDto,
  type CreateStageDto,
  type JobPipeline,
} from '@/types/pipeline';
import type { PipelineStageDraft } from './PipelineFormDrawer';

export function stageDraftsFromPipeline(pipeline: JobPipeline | null): PipelineStageDraft[] {
  return (pipeline?.stages ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((stage) => ({
      draftId: stage.id,
      id: stage.id,
      name: stage.name,
      corePhase: stage.corePhase,
      color: stage.color ?? CORE_PHASE_COLORS[stage.corePhase],
      isTerminal: Boolean(stage.isTerminal),
      trigger: stage.trigger ?? null,
      triggerQualifier: stage.triggerQualifier ?? '',
    }));
}

// One definition, shared with the job-facing surfaces that now print the core
// phase beside a tenant's custom stage name — the settings drawer and the job
// page must not spell the same phase two ways.
export { formatCorePhase } from '@/lib/jobs/jobPipeline';

export function stageDraftTriggerPayload(
  stage: PipelineStageDraft,
): Pick<CreateStageDto, 'trigger' | 'triggerQualifier'> {
  const trigger = stage.trigger ?? null;
  return {
    trigger,
    triggerQualifier: trigger ? stage.triggerQualifier?.trim().toUpperCase() || null : null,
  };
}

export function stageDraftTriggerChanged(
  previous: PipelineStageDraft,
  next: PipelineStageDraft,
): boolean {
  const previousTrigger = stageDraftTriggerPayload(previous);
  const nextTrigger = stageDraftTriggerPayload(next);
  return (
    previousTrigger.trigger !== nextTrigger.trigger ||
    previousTrigger.triggerQualifier !== nextTrigger.triggerQualifier
  );
}

export function stageDraftsToCreatePayload(
  stages: PipelineStageDraft[],
): NonNullable<CreatePipelineDto['stages']> {
  return stages.map((stage, index) => ({
    name: stage.name,
    corePhase: stage.corePhase,
    color: stage.color,
    isTerminal: stage.isTerminal,
    ...stageDraftTriggerPayload(stage),
    sortOrder: index,
  }));
}
